/* global Swiper */

(() => {
	/**
	 * ==========================================
	 * Shopify Storefront API 設定
	 * ==========================================
	 */
	const STORE_DOMAIN = 'reignite-7657.myshopify.com';
	const API_VERSION = '2024-04';
	const STOREFRONT_PUBLIC_TOKEN = 'e290df8e0b67b06f02a69cf4bc7f996c';

	/**
	 * ==========================================
	 * 商品表示設定
	 * ==========================================
	 *
	 * 商品取得ルール
	 *
	 * ＜sortKey変更例＞
	 * - BEST_SELLING : 売れている順
	 * - CREATED_AT : 作成日時順
	 * - ID : ID順
	 * - PRICE : 価格順
	 * - PRODUCT_TYPE : 商品タイプ順
	 * - RELEVANCE : 検索キーワードとの関連度順（query指定時のみ）
	 * - TITLE : タイトル順
	 * - UPDATED_AT : 更新日時順
	 * - VENDOR : ベンダー（ブランド）順
	 *
	 * ＜reverse＞
	 * - false : 昇順
	 * - true : 降順
	 */

	const PRODUCT_LIMIT = 8; // 表示件数
	const FETCH_LIMIT = 30; // 取得件数(SOLD OUT考慮)
	const PRODUCT_SORT_KEY = 'CREATED_AT'; // 並び順・sortKeyを追加する
	// const PRODUCT_SORT_KEY = 'COLLECTION_DEFAULT'; // 並び順・特定のコレクションを指定する場合は有効にする
	const PRODUCT_REVERSE = true; // true=降順 / false=昇順
	const SOLDOUT_LABEL = 'SOLD OUT'; // 売り切れ文言

	/**
	 * ==========================================
	 * コレクション設定
	 * ==========================================
	 *
	 * 特定コレクションの商品を表示する場合
	 * handle を指定してください。
	 *
	 * 全商品を表示する場合は
	 * COLLECTION_HANDLE をコメントアウトしてください。
	 */

	// 特定コレクションを指定する場合は有効にする
	// const COLLECTION_HANDLE = '2025-a-w';

	// 全商品を表示する場合は有効にする
	const COLLECTION_HANDLE = null;

	// ==========================================
	// Shopify商品データ保持
	// ==========================================
	let shopifyProducts = null;

	/**
	 * ==========================================
	 * Shopifyから商品一覧を取得
	 * ==========================================
	 */
	async function fetchShopifyProducts() {

		/**
		 * ==========================================
		 * コレクション指定あり
		 * ==========================================
		 */
		const collectionQuery = `
        query GetCollectionProducts(
        $handle: String!
        $first: Int!
        ) {
			collection(handle: $handle) {
				products(
				first: $first
				sortKey: ${PRODUCT_SORT_KEY}
				reverse: ${PRODUCT_REVERSE}
				) {
					edges {
						node {
							id
							title
							handle
							onlineStoreUrl
							availableForSale
							tags

							featuredImage {
								altText
								url
							}

							priceRange {
								minVariantPrice {
									amount
									currencyCode
								}
							}
						}
					}
				}
			}
        }
    `;

		/**
		 * ==========================================
		 * 全商品取得
		 * ==========================================
		 */
		const allProductsQuery = `
        query GetLatestProducts($first: Int!) {
        products(
            first: $first
            sortKey: ${PRODUCT_SORT_KEY}
            reverse: ${PRODUCT_REVERSE}
        ) {
            edges {
			node {
					id
					title
					handle
					onlineStoreUrl
					availableForSale
					tags

					featuredImage {
						altText
						url
					}

					priceRange {
						minVariantPrice {
							amount
							currencyCode
						}
					}
				}
            }
        }
        }
    `;

		// コレクション指定の有無でクエリ切替
		const query = COLLECTION_HANDLE
			? collectionQuery
			: allProductsQuery;

		const variables = COLLECTION_HANDLE
			? {
				handle: COLLECTION_HANDLE,
				first: FETCH_LIMIT,
			}
			: {
				first: FETCH_LIMIT,
			};

		try {

			const res = await fetch(
				`https://${STORE_DOMAIN}/api/${API_VERSION}/graphql.json`,
				{
					method: 'POST',
					headers: {
						'Content-Type': 'application/json',
						'X-Shopify-Storefront-Access-Token':
							STOREFRONT_PUBLIC_TOKEN,
					},
					body: JSON.stringify({
						query,
						variables,
					}),
				}
			);

			const { data, errors } = await res.json();

			if (errors) {
				return;
			}

			/**
			 * ==========================================
			 * 商品取得
			 * ==========================================
			 */

			let productEdges = [];

			if (COLLECTION_HANDLE) {
				if (!data.collection) {
					return;
				}

				productEdges = data.collection.products.edges;
			} else {
				productEdges = data.products.edges;
			}

			// 在庫あり + 限定公開商品を除外
			productEdges = productEdges.filter(
				({ node }) =>
					node.availableForSale &&
					!(node.tags || []).includes('limited')
			);

			// 8件だけ表示
			productEdges = productEdges.slice(0, PRODUCT_LIMIT);

			// 商品データを返す
			return productEdges;

		} catch (error) {
			// eslint-disable-next-line no-console
			console.error(
				'商品取得中にエラーが発生しました',
				error
			);

		}
	}

	/**
	 * ==========================================
	 * 商品一覧を画面に描画
	 * ==========================================
	 */
	function renderProducts(edges) {

		/**
		 * 出力先
		 * <div id="shopify-products" data-shopify-products></div>
		 */
		const container = document.querySelector('[data-shopify-products]');

		if (!container) return;

		// 初期化
		container.innerHTML = '';

		const wrapper = document.createElement('div');
		wrapper.className = 'swiper-wrapper';

		const prev = document.createElement('div');
		prev.className = 'swiper-button-prev';

		const next = document.createElement('div');
		next.className = 'swiper-button-next';

		container.appendChild(prev);
		container.appendChild(next);

		container.appendChild(wrapper);

		edges.forEach(({ node }) => {

			// 商品データ
			const product = node;

			const title = product.title;
			const handle = product.handle;

			/**
			 * Shopifyの商品URL
			 *
			 * onlineStoreUrl が取得できない場合は
			 * handleからURLを生成
			 */
			const onlineUrl =
				product.onlineStoreUrl ||
				`https://${STORE_DOMAIN}/products/${handle}`;

			const image = product.featuredImage;
			const minPrice =
				product.priceRange?.minVariantPrice;

			const amount = minPrice?.amount;
			const currency = minPrice?.currencyCode;

			/**
			 * 販売状態
			 *
			 * true  = 販売中
			 * false = 売り切れ
			 */
			const isAvailable =
				product.availableForSale;

			/**
			 * 商品カード
			 */
			const slide = document.createElement('div');
			slide.className = 'swiper-slide';

			const item = document.createElement('a');
			item.className = 'shopify-product';

			item.href = onlineUrl;
			item.target = '_blank';
			item.rel = 'noopener';

			slide.appendChild(item);

			/**
			 * ==========================================
			 * 商品画像
			 * ==========================================
			 */
			if (image) {

				const imgWrapper =
					document.createElement('div');

				imgWrapper.className =
					'shopify-product__image';

				const img = document.createElement('img');

				img.src = image.url;
				img.alt = image.altText || title;
				img.className = 'shopify-product__img';

				imgWrapper.appendChild(img);

				// 売り切れバッジ表示
				if (!isAvailable) {

					const badge =
						document.createElement('span');

					badge.className =
						'shopify-product__badge-soldout';

					badge.textContent =
						SOLDOUT_LABEL;

					imgWrapper.appendChild(badge);
				}

				item.appendChild(imgWrapper);
			}

			/**
			 * ==========================================
			 * 商品タイトル
			 * ==========================================
			 */
			const titleEl = document.createElement('p');
			titleEl.className = 'shopify-product__ttl';
			titleEl.textContent = title;

			item.appendChild(titleEl);

			/**
			 * ==========================================
			 * 商品価格
			 * ==========================================
			 */
			if (amount != null) {

				const priceEl =
					document.createElement('p');

				priceEl.className =
					'shopify-product__price';

				if (currency === 'JPY') {
					priceEl.textContent =
						`¥${Number(amount).toLocaleString('ja-JP')}`;
				} else {
					priceEl.textContent =
						`${currency} ${Number(amount).toFixed(2)}`;
				}

				item.appendChild(priceEl);
			}

			// 商品カード追加
			wrapper.appendChild(slide);

		});

		new Swiper('[data-shopify-products]', {
			loop: true,
			speed: 1000,
			watchOverflow: true,
			slidesPerView: 'auto',

			navigation: {
				nextEl: '.swiper-button-next',
				prevEl: '.swiper-button-prev',
			},
		});

	}
	// ============================================
	// 実行
	// ============================================

	// STOREセクション取得
	const store = document.querySelector('[data-store-area]');

	/**
	 * ==========================================
	 * loadingComplete後にShopify商品を先読み
	 * ==========================================
	 *
	 * 初回表示を軽くするため、
	 * ローディング終了後に通信だけ開始する。
	 * （まだ画面には描画しない）
	 */
	document.addEventListener('loadingComplete', () => {

		requestAnimationFrame(() => {

			setTimeout(async () => {

				// 商品データだけ取得して保持
				shopifyProducts = await fetchShopifyProducts();

			}, 1000);

		});

	});

	if (store) {

		/**
		 * ==========================================
		 * STORE表示時に商品を描画
		 * ==========================================
		 *
		 * 通信済みなら即描画。
		 * まだ通信中なら取得完了後に描画する。
		 */
		const observer = new IntersectionObserver(async (entries) => {

			// STOREが画面付近に来るまで何もしない
			if (!entries[0].isIntersecting) {
				return;
			}

			// まだ取得できていない場合はここで取得
			if (!shopifyProducts) {
				shopifyProducts = await fetchShopifyProducts();
			}

			// 商品を描画
			renderProducts(shopifyProducts);

			// 一度描画したら監視終了
			observer.disconnect();

		}, {
			// 画面に表示される500px手前で描画開始
			rootMargin: '500px'
		});

		observer.observe(store);

	}

})();
